import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "./Reveal";
import { api } from "@/lib/api";
import { X, Check, Loader2, ArrowUpRight, ExternalLink } from "lucide-react";
import { useContent } from "@/context/ContentContext";

export const Works = () => {
  const w0 = useContent().works;
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api
      .get("/works")
      .then((res) => setWorks(res.data))
      .catch(() => setWorks([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="works" data-testid="works-section" className="bg-ink py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-gold mb-4">{w0.eyebrow}</p>
          <h2 className="font-jp text-3xl sm:text-5xl font-bold tracking-tight chrome-text max-w-2xl leading-tight whitespace-pre-line">
            {w0.heading}
          </h2>
          <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-white/60">
            {w0.description}
          </p>
        </Reveal>

        {loading ? (
          <div className="py-24 flex justify-center" data-testid="works-loading">
            <Loader2 className="animate-spin text-gold" />
          </div>
        ) : works.length === 0 ? (
          <div className="py-24 text-center text-white/40" data-testid="works-empty">実績はまだ登録されていません。</div>
        ) : (
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {works.map((w, i) => (
              <Reveal key={w.id} delay={(i % 3) * 0.06}>
                <button
                  type="button"
                  onClick={() => setSelected(w)}
                  data-testid={`work-card-${i}`}
                  className="hud group relative w-full text-left overflow-hidden rounded-2xl border border-white/10 bg-black/40 transition-all duration-500 hover:-translate-y-1 hover:border-gold/40 hover:glow-cyan focus:outline-none"
                >
                  <span className="hud-tl" />
                  <span className="hud-br" />
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={w.image}
                      alt={w.title}
                      className="h-full w-full object-cover opacity-40 grayscale transition-all duration-700 group-hover:opacity-70 group-hover:grayscale-0 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                    <span className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.2em] uppercase text-gold bg-black/50 border border-gold/30 rounded-full px-3 py-1">
                      {w.category}
                    </span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-mono text-xs text-white/40">{w.year}</span>
                      <ArrowUpRight className="text-white/30 transition-all duration-300 group-hover:text-gold group-hover:translate-x-1 group-hover:-translate-y-1" size={18} />
                    </div>
                    <h3 className="font-jp text-xl font-bold text-white mb-2">{w.title}</h3>
                    <p className="text-sm font-light leading-relaxed text-white/60">{w.summary}</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
            data-testid="work-modal"
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-md p-6"
          >
            <motion.div
              initial={{ y: 30, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="hud relative w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-2xl border border-gold/30 bg-surface glow-cyan"
            >
              <span className="hud-tl" />
              <span className="hud-br" />
              <button
                onClick={() => setSelected(null)}
                data-testid="work-modal-close"
                className="absolute top-5 right-5 z-10 grid place-items-center h-9 w-9 rounded-full bg-black/50 text-white/70 hover:text-gold transition-colors"
              >
                <X size={20} />
              </button>
              <div className="relative h-64 md:h-72 overflow-hidden rounded-t-2xl">
                <img src={selected.image} alt={selected.title} className="h-full w-full object-cover opacity-80" />
                <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/40 to-transparent" />
                <span className="absolute bottom-4 left-8 md:left-10 font-mono text-[10px] tracking-[0.25em] uppercase text-gold">Case Study</span>
              </div>
              <div className="p-8 md:p-10 -mt-6 relative">
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-gold border border-gold/30 rounded-full px-3 py-1">{selected.category}</span>
                  <span className="font-mono text-xs text-white/40">{selected.year}</span>
                </div>
                <h3 className="font-jp text-3xl md:text-4xl font-bold tracking-tight chrome-text leading-tight">{selected.title}</h3>

                {selected.summary && (
                  <p className="mt-6 font-jp text-lg font-light leading-loose text-white/90 whitespace-pre-line">{selected.summary}</p>
                )}

                <div className="hairline my-7" />

                <p className="font-jp text-base font-light leading-loose text-white/65 whitespace-pre-line">{selected.description}</p>

                {selected.tech?.length > 0 && (
                  <div className="mt-9">
                    <p className="font-mono text-xs tracking-[0.2em] uppercase text-white/40 mb-3">Tech Stack</p>
                    <div className="flex flex-wrap gap-2">
                      {selected.tech.map((t) => (
                        <span key={t} className="flex items-center gap-1.5 text-xs text-white/80 border border-white/15 rounded-full px-3 py-1.5">
                          <Check size={12} className="text-gold" /> {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {selected.url && (
                  <a
                    href={selected.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="work-modal-link"
                    className="group mt-9 inline-flex items-center gap-2.5 rounded-full bg-gold px-7 py-3.5 text-sm font-medium text-ink hover:bg-gold-light glow-cyan transition-colors duration-300"
                  >
                    サイトを見る
                    <ExternalLink size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
